import React, { useState } from "react";
import { useLang } from "./LanguageProvider";
import { ArrowUpRight } from "lucide-react";
import Lightbox from "./Lightbox";

// Real certificate images from old alwaabplastics CDN — paired with the credential they represent.
// Each credential card now embeds the actual document(s) and opens them in the lightbox.
const CERTS = [
  {
    name_en: "Lubrizol FlowGuard™ Licensee",
    name_ar: "ترخيص Lubrizol FlowGuard™",
    body_en: "Sole authorised manufacturer in the State of Qatar.",
    body_ar: "المصنّع الوحيد المرخّص في دولة قطر.",
    docs: [
      "https://www.alwaabplastics.com/images/certificates/alwaab-cpvc-certificates-1.jpg",
      "https://www.alwaabplastics.com/images/certificates/alwaab-cpvc-certificates-2.jpg",
    ],
  },
  {
    name_en: "NSF / ANSI 61",
    name_ar: "NSF / ANSI 61",
    body_en: "Certified safe for direct drinking-water contact.",
    body_ar: "معتمد لتلامس مياه الشرب.",
    docs: [
      "https://www.alwaabplastics.com/images/certificates/alwaab-cpvc-certificates-3.jpg",
    ],
  },
  {
    name_en: "WRAS Approved",
    name_ar: "WRAS Approved",
    body_en: "UK Water Regulations Advisory Scheme: material approval.",
    body_ar: "اعتماد المواد البريطاني للوائح المياه.",
    docs: [
      "https://www.alwaabplastics.com/images/certificates/alwaab-cpvc-certificates-4.jpg",
    ],
  },
  {
    name_en: "ISO 9001:2015",
    name_ar: "ISO 9001:2015",
    body_en: "Quality management, independently audited.",
    body_ar: "إدارة الجودة، تدقيق مستقل.",
    docs: [
      "https://www.alwaabplastics.com/images/certificates/alwaab-cpvc-certificates-5.jpg",
      "https://www.alwaabplastics.com/images/certificates/alwaab-cpvc-certificates-6.jpg",
    ],
  },
  {
    name_en: "DIN EN ISO 15877",
    name_ar: "DIN EN ISO 15877",
    body_en: "European standard for CPVC plumbing systems.",
    body_ar: "المعيار الأوروبي لأنظمة CPVC.",
    docs: [
      "https://www.alwaabplastics.com/images/certificates/alwaab-cpvc-certificates-7.jpg",
      "https://www.alwaabplastics.com/images/certificates/alwaab-cpvc-certificates-8.jpg",
    ],
  },
  {
    name_en: "QCS 2014",
    name_ar: "QCS 2014",
    body_en: "Qatar Construction Specification, compliant for potable water.",
    body_ar: "مطابق لمواصفات البناء القطرية لمياه الشرب.",
    docs: [
      "https://www.alwaabplastics.com/images/certificates/alwaab-cpvc-certificates-9.jpg",
      "https://www.alwaabplastics.com/images/certificates/alwaab-cpvc-certificates-10.jpg",
      "https://www.alwaabplastics.com/images/certificates/alwaab-cpvc-certificates-11.jpg",
      "https://www.alwaabplastics.com/images/certificates/alwaab-cpvc-certificates-12.jpg",
      "https://www.alwaabplastics.com/images/certificates/alwaab-cpvc-certificates-13.jpg",
    ],
  },
];

export default function Certifications() {
  const { t, lang, isRtl } = useLang();
  const [lbItems, setLbItems] = useState([]);
  const [lbIdx, setLbIdx] = useState(null);

  const openCert = (cert, startIdx = 0) => {
    const items = cert.docs.map((src, i) => ({
      src,
      caption: `${lang === "ar" ? cert.name_ar : cert.name_en}${cert.docs.length > 1 ? ` · ${i + 1}/${cert.docs.length}` : ""}`,
      subcaption: lang === "ar" ? "FlowWhite · وثائق الاعتماد" : "FlowWhite · Credential Document",
    }));
    setLbItems(items);
    setLbIdx(startIdx);
  };

  return (
    <section
      id="certifications"
      data-testid="certifications-section"
      className="relative bg-brand-navy py-24 md:py-40 border-t border-white/5"
    >
      <div className="container mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14 md:mb-20">
          <div className="lg:col-span-5 reveal">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-brand-teal" />
              <span className="text-[11px] uppercase tracking-[0.28em] text-brand-teal font-semibold">
                {t.certs.overline}
              </span>
            </div>
            <h2 className="font-heading text-white text-3xl sm:text-4xl md:text-5xl lg:text-[56px] leading-[1.05] font-medium tracking-tight">
              {t.certs.title}
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 reveal reveal-delay-1 flex items-end">
            <p className={`text-white/55 text-sm md:text-base leading-relaxed ${isRtl ? "text-right" : "text-left"}`}>
              {isRtl
                ? "كل اعتماد أدناه مدعوم بوثائق رسمية صادرة عن الجهة المُصدِرة. اضغط على أي بطاقة لمعاينة الشهادة الأصلية."
                : "Every credential below is backed by an officially issued document from the certifying body. Tap any card to view the original certificate."}
            </p>
          </div>
        </div>

        {/* Credential cards — each with embedded actual document(s) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {CERTS.map((c, i) => (
            <button
              key={i}
              type="button"
              onClick={() => openCert(c, 0)}
              data-testid={`cert-item-${i}`}
              className="group relative text-left bg-brand-surface hover:bg-brand-surfaceHover border border-white/8 hover:border-brand-teal/40 rounded-2xl overflow-hidden transition-all duration-500 flex flex-col"
            >
              {/* Document preview — actual cert image */}
              <div className="relative aspect-[4/3] overflow-hidden bg-white/[0.03]">
                <img
                  src={c.docs[0]}
                  alt={lang === "ar" ? c.name_ar : c.name_en}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform [transition-duration:1400ms] group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-surface via-brand-surface/10 to-transparent" />
                {c.docs.length > 1 && (
                  <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-brand-navy/85 backdrop-blur-md border border-white/15 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-white/85 font-semibold">
                    +{c.docs.length - 1}
                  </div>
                )}
                <div className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-white/70 font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-teal" />
                  {String(i + 1).padStart(2, "0")} / 0{CERTS.length}
                </div>
              </div>

              {/* Card body */}
              <div className="p-6 md:p-7 flex-1 flex flex-col">
                <h3 className={`font-heading text-white text-lg md:text-xl font-medium leading-tight mb-3 ${isRtl ? "text-right" : "text-left"}`}>
                  {lang === "ar" ? c.name_ar : c.name_en}
                </h3>
                <p className={`text-white/55 text-sm leading-relaxed flex-1 ${isRtl ? "text-right" : "text-left"}`}>
                  {lang === "ar" ? c.body_ar : c.body_en}
                </p>
                <div className="mt-5 pt-5 border-t border-white/8 flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.28em] text-white/40 font-semibold">
                    {isRtl
                      ? c.docs.length > 1 ? `${c.docs.length} وثائق` : "وثيقة رسمية"
                      : c.docs.length > 1 ? `${c.docs.length} documents` : "Official document"}
                  </span>
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/15 group-hover:border-brand-teal group-hover:bg-brand-teal text-white/70 group-hover:text-white transition-all duration-300">
                    <ArrowUpRight size={14} />
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <Lightbox
        items={lbItems}
        index={lbIdx}
        onClose={() => setLbIdx(null)}
        onPrev={() => setLbIdx((i) => (i - 1 + lbItems.length) % lbItems.length)}
        onNext={() => setLbIdx((i) => (i + 1) % lbItems.length)}
      />
    </section>
  );
}
